import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Copy, MoreVertical, Pencil, Plus, Save, Star, Trash2, Undo2 } from "lucide-react";
import GlassMenu from "./GlassMenu";
import { validatePresetName } from "@/lib/autoSourcing/presets";
import "./AutoSourcing.css";

export function ConfirmDelete({ preset, onCancel, onConfirm, busy }) {
  const cancelRef = useRef(null);
  useEffect(() => {
    cancelRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);
  return createPortal(
    <div className="as-dialog-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onCancel()}>
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="as-delete-title"
        aria-describedby="as-delete-desc"
        className="as-glass-menu as-dialog"
        data-testid="preset-delete-dialog"
      >
        <div id="as-delete-title" className="as-dialog-title">Delete “{preset.name}”?</div>
        <div id="as-delete-desc" className="as-dialog-text">This cannot be undone.</div>
        <div className="as-dialog-actions">
          <button ref={cancelRef} type="button" className="as-cta as-cta-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="as-cta as-cta-danger"
            onClick={onConfirm}
            disabled={busy}
            data-testid="preset-delete-confirm"
          >
            Delete
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function RenameInput({ initial, onSubmit, onCancel }) {
  const [value, setValue] = useState(initial);
  const [error, setError] = useState(null);
  const ref = useRef(null);
  const doneRef = useRef(false);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  const submit = async ({ fromBlur = false } = {}) => {
    if (doneRef.current) return;
    const check = validatePresetName(value);
    if (!check.ok) {
      if (fromBlur) {
        doneRef.current = true;
        onCancel();
      } else {
        setError(check.error);
      }
      return;
    }
    doneRef.current = true;
    if (check.name === initial) {
      onCancel();
      return;
    }
    const ok = await onSubmit(check.name);
    if (!ok) doneRef.current = false;
  };
  return (
    <span className="as-preset-rename">
      <input
        ref={ref}
        value={value}
        maxLength={200}
        aria-label="Preset name"
        aria-invalid={error ? true : undefined}
        onChange={(e) => { setValue(e.target.value); setError(null); }}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); void submit(); }
          else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); doneRef.current = true; onCancel(); }
        }}
        onBlur={() => void submit({ fromBlur: true })}
        data-testid="preset-rename-input"
      />
      {error ? <span className="as-preset-rename-error" role="alert">{error}</span> : null}
    </span>
  );
}

function UserPresetChip({ preset, active, dirty, actions }) {
  const moreRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const closeMenu = useCallback(({ focusAnchor } = {}) => {
    setMenuOpen(false);
    if (focusAnchor) moreRef.current?.focus();
  }, []);

  const items = [
    { key: "rename", label: "Rename", icon: Pencil, onSelect: () => setRenaming(true) },
    preset.is_default
      ? { key: "default", label: "Remove default", icon: Star, onSelect: () => actions.onToggleDefault(preset) }
      : { key: "default", label: "Mark as default", icon: Star, onSelect: () => actions.onToggleDefault(preset) },
    { key: "clone", label: "Clone", icon: Copy, onSelect: () => actions.onClone(preset) },
    ...(active && dirty
      ? [
          { key: "save", label: "Save changes", icon: Save, separatorBefore: true, onSelect: actions.onSaveChanges },
          { key: "save-new", label: "Save as new", icon: Plus, onSelect: actions.onSaveAsNew },
        ]
      : []),
    { key: "delete", label: "Delete preset", icon: Trash2, danger: true, separatorBefore: true, onSelect: () => actions.onRequestDelete(preset) },
  ];

  return (
    <div
      className={`as-preset-chip ${active ? "active" : ""} ${active && dirty ? "dirty" : ""}`}
      data-testid={`preset-chip-${preset.profile_id}`}
    >
      {renaming ? (
        <RenameInput
          initial={preset.name}
          onCancel={() => setRenaming(false)}
          onSubmit={async (name) => {
            const ok = await actions.onRename(preset, name);
            if (ok) setRenaming(false);
            return ok;
          }}
        />
      ) : (
        <button
          type="button"
          className="as-preset-chip-name"
          aria-pressed={active}
          title={preset.name}
          onClick={() => actions.onLoad(preset)}
        >
          <span className="as-preset-chip-label">{preset.name}</span>
          {preset.is_default ? (
            <Star className="as-preset-star" aria-label="Default" data-testid="preset-default-star" />
          ) : null}
          {active ? <Check className="as-preset-check" aria-hidden="true" /> : null}
        </button>
      )}
      <button
        ref={moreRef}
        type="button"
        className="as-preset-chip-more"
        aria-label={`Preset actions for ${preset.name}`}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((v) => !v)}
        data-testid={`preset-more-${preset.profile_id}`}
      >
        <MoreVertical aria-hidden="true" />
      </button>
      <GlassMenu
        anchorRef={moreRef}
        open={menuOpen}
        onClose={closeMenu}
        items={items}
        label={`${preset.name} actions`}
        testId="preset-menu"
      />
    </div>
  );
}

export function UnsavedChanges({ onSaveChanges, onSaveAsNew, onDiscard }) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const close = useCallback(({ focusAnchor } = {}) => {
    setOpen(false);
    if (focusAnchor) ref.current?.focus();
  }, []);
  return (
    <>
      <button
        ref={ref}
        type="button"
        className="as-unsaved"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        data-testid="auto-sourcing-unsaved"
      >
        <Save aria-hidden="true" /> Unsaved changes <ChevronDown aria-hidden="true" />
      </button>
      <GlassMenu
        anchorRef={ref}
        open={open}
        onClose={close}
        label="Unsaved changes"
        testId="unsaved-menu"
        items={[
          { key: "save", label: "Save changes", icon: Save, onSelect: onSaveChanges },
          { key: "save-new", label: "Save as new", icon: Plus, onSelect: onSaveAsNew },
          { key: "discard", label: "Discard changes", icon: Undo2, separatorBefore: true, onSelect: onDiscard },
        ]}
      />
    </>
  );
}

/**
 * Quick Setup presets: built-in SYSTEM presets (apply-only) and the user's
 * saved presets (load, rename, default, clone, save changes, delete).
 */
export default function PresetBar({
  systemPresets,
  activeSystemId,
  systemModified,
  onApplySystem,
  presets,
  activePresetId,
  dirty,
  actions,
  onSaveCurrent,
}) {

  return (
    <div className="as-presets" data-testid="auto-sourcing-preset-bar">
      <div className="as-presets-label">System</div>
      <div className="as-retailer-tabs as-presets-row" data-testid="auto-sourcing-presets">
        {systemPresets.map((p) => (
          <button
            key={p.id}
            type="button"
            className={!activePresetId && !systemModified && activeSystemId === p.id ? "active" : ""}
            aria-pressed={!activePresetId && !systemModified && activeSystemId === p.id}
            onClick={() => onApplySystem(p)}
          >
            {p.name}
          </button>
        ))}
        {!activePresetId && systemModified ? (
          <span className="as-preset-custom" data-testid="auto-sourcing-preset-custom">Custom</span>
        ) : null}
      </div>

      <div className="as-presets-label as-presets-label-mine">
        My presets
        {activePresetId && dirty ? (
          <UnsavedChanges
            onSaveChanges={actions.onSaveChanges}
            onSaveAsNew={actions.onSaveAsNew}
            onDiscard={actions.onDiscard}
          />
        ) : null}
      </div>
      <div className="as-presets-mine" data-testid="auto-sourcing-my-presets">
        {presets.map((p) => (
          <UserPresetChip
            key={p.profile_id}
            preset={p}
            active={activePresetId === p.profile_id}
            dirty={dirty}
            actions={actions}
          />
        ))}
        <button
          type="button"
          className="as-preset-add"
          onClick={onSaveCurrent}
          data-testid="auto-sourcing-save-current"
        >
          <Plus aria-hidden="true" /> Save current setup
        </button>
      </div>

    </div>
  );
}
